import React, { FC, useEffect, useState } from 'react';
import './styles.scss';
import {
  BaseModal,
  MultiselectItem,
  Button,
  Input,
  Dropdown,
  DropdownList,
  DropdownItem,
  ToastMessage,
  ToastType
} from '@cokitchen/cokitchen-components';
import { ReactComponent as FolderIcon } from 'assets/icons/folder-download.svg';
import { InventoryService } from 'services/inventory.services';
import { STORAGE_KEYS } from 'utils/constants';
import { ArrowDownIcon, CloseOutlineIcon } from 'cokitchen-icons';
import { toast } from 'react-toastify';

interface ExportInventoryModalProps {
  show: boolean;
  options: MultiselectItem[];
  onClose: () => void;
}

const ExportInventoryModal: FC<ExportInventoryModalProps> = ({ show, options, onClose }) => {
  const [values, setValues] = useState<MultiselectItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState<string>('');
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (show) {
      setLoading(false);
      setValues([]);
      setEmail(localStorage.getItem(STORAGE_KEYS.EMAIL) ?? '');
    }
  }, [show]);

  const handleSubmitForm = async () => {
    setLoading(true);
    const brand_ids = getRealValues();
    const alertData: { type: ToastType; message: string } = { type: 'success', message: '' };
    try {
      const resp = await InventoryService.exportCSV({ brand_ids, email });
      alertData.message = resp.message;
    } catch (error: any) {
      alertData.message = error.message;
      alertData.type = 'error';
    } finally {
      onClose();
      toast(<ToastMessage {...alertData} />);
    }
  };

  const getRealValues = () => {
    let returnVal = [...values.map(({ value }) => value)];
    if (returnVal[0] === 'all') {
      returnVal = options.reduce((acc: MultiselectItem[], { value }) => {
        if (value !== 'all') acc.push(value);
        return acc;
      }, []);
    }
    return returnVal;
  };

  const handleSelectBrand = (option: MultiselectItem) => {
    if (option.value === 'all') {
      setValues([{ value: 'all', text: 'All' }]);
      setShowDropdown(false);
      return;
    }
    const allValueIndex = values.findIndex((v) => v.value === 'all');
    const newList = [...values];
    if (allValueIndex !== -1) {
      newList.splice(allValueIndex, 1);
    }
    const brandIndex = values.findIndex((b) => b.value === option.value);
    if (brandIndex === -1) {
      newList.push(option);
    } else {
      newList.splice(brandIndex, 1);
    }
    setValues([...newList]);
  };

  const deleteMetadataTag = (option: MultiselectItem) => {
    setValues(values.filter((item) => item.value !== option.value));
  };

  const isBrandSelected = (option: MultiselectItem) => {
    const valueIds = values.map((o) => o.value);
    return valueIds.includes(option.value);
  };

  return (
    <BaseModal
      isOpen={show}
      title='Export Inventory Items'
      description='The csv file containing inventory count will be sent to your inputted email. Select the brands you want to export.'
      titleIcon={<FolderIcon />}
      titleIconColor='info'
      onRequestClose={onClose}
      footer={
        <Button
          color='primary'
          variant='block'
          className='mx-auto'
          loading={loading}
          disabled={email === '' || values.length === 0}
          onClick={handleSubmitForm}>
          Confirm
        </Button>
      }>
      <div className='mt-2'>
        <Input
          type='email'
          label='Email'
          placeholder='Enter Email'
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className='mt-3 mb-4'
        />
        <label className='font-medium mb-1'>Select brand</label>

        <Dropdown
          placement='bottom-start'
          visible={showDropdown}
          onClickOutside={() => setShowDropdown(false)}
          content={
            <div
              onClick={() => setShowDropdown(!showDropdown)}
              className={`w-full rounded-md border-[1px] border-[#E6E5E5] p-4 flex justify-between items-center bg-white`}>
              {values.length > 0 ? (
                <div className='flex gap-2'>
                  {values[0].value !== 'all' ? (
                    <ul className='flex gap-1 flex-wrap'>
                      {values.map((item) => {
                        return (
                          <li
                            key={item.value}
                            className='text-sm leading-[19px] py-1 font-medium flex gap-1 items-center rounded-[100px] px-2 cursor-pointer text-gray-5 bg-[#F2F2F3]'>
                            <span className='capitalize'> {item.text}</span>
                            <span
                              role='button'
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteMetadataTag(item);
                              }}>
                              <CloseOutlineIcon />
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <span className='text-sm'>All</span>
                  )}
                </div>
              ) : (
                <p className='text-[#A6A6A6]'>Select brands</p>
              )}

              <ArrowDownIcon className='text-[#A6A6A6]' />
            </div>
          }>
          <DropdownList className={`min-w-[150px]`}>
            {options.map((o) => (
              <DropdownItem key={o.value} onClick={() => handleSelectBrand(o)}>
                <Input
                  type='checkbox'
                  className='mr-2 pointer-events-none'
                  readOnly
                  label={o.text}
                  checked={isBrandSelected(o)}
                />
              </DropdownItem>
            ))}
          </DropdownList>
        </Dropdown>
      </div>
    </BaseModal>
  );
};

export default ExportInventoryModal;
